import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { Eyebrow } from './kit';

/**
 * The portal's one drawer.
 *
 * Every side panel in the dashboard goes through here — detail views, settings,
 * short forms. Before this existed each screen hand-rolled its own: three
 * different backdrops, four z-index scales, inline box-shadows, and a `«`
 * chevron doing the job of a close button. The chrome is the same everywhere
 * now so the only thing a screen decides is what goes inside.
 *
 * The language is the kit's: hairline rules, a monospace eyebrow over a display
 * title, ink backdrop — the same wash the mobile nav uses, since a drawer is
 * the same gesture. No stacked shadows, no gradients.
 *
 * Three things it does that the hand-rolled versions did not:
 *
 * - **Portals to `<body>`.** The dashboard wraps page content in `relative
 *   z-[1]`, which is its own stacking context — a drawer rendered inside it can
 *   never paint above the `z-50` sidebar however high its own z-index climbs.
 * - **Escape closes it, and the page behind stops scrolling.** Both were
 *   already promised by a `title="Close (Escape)"` tooltip that did nothing.
 * - **`100dvh`, not `100vh`.** On mobile Safari the viewport unit runs under
 *   the URL bar and takes the footer actions with it.
 *
 * `footer` is pinned below the scroll area rather than sitting at the end of
 * the content: the decision a drawer asks for should never be something you
 * have to scroll to find.
 */

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: React.ReactNode;
  /** Monospace kicker above the title — what kind of thing this is. */
  eyebrow?: string;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  /** Pinned action row. Stays visible while the body scrolls. */
  footer?: React.ReactNode;
  /** Extra controls on the header rule, left of the close button. */
  headerActions?: React.ReactNode;
  width?: 'sm' | 'md' | 'lg' | 'xl';
  /** Drop the body padding — for content that manages its own gutters. */
  bare?: boolean;
}

const widthClasses = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-[43rem]',
};

/** Open drawers, so nested/stacked panels don't unlock the page early. */
let scrollLocks = 0;

const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  title,
  eyebrow,
  subtitle,
  children,
  footer,
  headerActions,
  width = 'md',
  bare = false,
}) => {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const titleId = React.useId();

  /*
    `onClose` is an inline arrow at every call site, so its identity changes on
    every render of the page holding the drawer — and those pages re-render
    constantly (react-query refetches, debounced auto-save). Depending on it
    directly would tear down and re-run the effects below on every one of those
    renders: focus would bounce to the trigger and back to the panel, ripping
    the caret out of whatever field was being typed in. A ref keeps the handler
    current without making it a dependency.
  */
  const onCloseRef = React.useRef(onClose);
  React.useEffect(() => {
    onCloseRef.current = onClose;
  });

  // Scroll lock and focus handoff: strictly once per open, never per render.
  React.useEffect(() => {
    if (!isOpen) return;

    const opener = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();

    const previousOverflow = document.body.style.overflow;
    scrollLocks += 1;
    document.body.style.overflow = 'hidden';

    return () => {
      scrollLocks -= 1;
      if (scrollLocks === 0) document.body.style.overflow = previousOverflow;
      opener?.focus?.();
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  // Both children are keyed: AnimatePresence tracks presence by key, and a bare
  // Fragment gives it nothing to track — it can end up holding the exiting copy
  // while the entering one mounts, showing two panels at once.
  return createPortal(
    <AnimatePresence>
      {isOpen && [
          <motion.div
            key="drawer-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[90] bg-ink/40 backdrop-blur-sm"
            onClick={onClose}
          />,

          <motion.div
            key="drawer-panel"
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            tabIndex={-1}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
            className={`fixed inset-y-0 right-0 z-[95] flex h-[100dvh] w-full flex-col border-l border-zinc-200 bg-white outline-none ${widthClasses[width]}`}
          >
            <div className="flex items-start justify-between gap-4 border-b border-zinc-200 px-6 py-5 sm:px-8">
              <div className="min-w-0">
                {eyebrow && <Eyebrow className="mb-2">{eyebrow}</Eyebrow>}
                <h2
                  id={titleId}
                  className="font-display text-xl font-bold leading-tight tracking-tight text-primary"
                >
                  {title}
                </h2>
                {subtitle && <p className="mt-1.5 text-[14px] text-muted">{subtitle}</p>}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {headerActions}
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  title="Close (Esc)"
                  className="-mr-2 rounded-full p-2 text-zinc-400 transition-colors hover:bg-zinc-900/5 hover:text-primary"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className={`flex-1 overflow-y-auto ${bare ? '' : 'px-6 py-6 sm:px-8'}`}>
              {children}
            </div>

            {footer && (
              <div className="border-t border-zinc-200 px-6 py-5 sm:px-8">{footer}</div>
            )}
          </motion.div>,
      ]}
    </AnimatePresence>,
    document.body
  );
};

export default Drawer;
